import dayjs from "dayjs";
import React from "react";
import BetaAlert from "../../../components/Beta/BetaAlert.tsx";
import GetListPaginatedList from "../../../components/Pagination/GetListPaginatedList.tsx";
import { Heading } from "../../../components/Theme/heading.tsx";
import {
  StackedListHeadingContent,
  StackedListThreeColumnItem,
} from "../../../components/Theme/stacked-list.tsx";
import PaginatedApiToCSVExportButton from "../../../components/utils/CSVExport/PaginatedApiToCSVExportButton.tsx";
import CopyToClipboard from "../../../components/utils/CopyToClipboard.tsx";
import { formatDate } from "../../../components/utils/HighlightNullAndOldDates.tsx";
import { useUpdateIssue } from "../../../hooks/updateEntity.tsx";
import {
  type GetAssetRegisterListAssetRegisterGetQueryParams,
  type GetIssuesIssueGetQueryParams,
  type IssueWithAssetRegister,
  getIssuesIssueGet,
  issueCategoriesEnum,
  issueSeverityEnum,
  useGetIssuesIssueGet,
} from "../../../kubb";
import { useRequiredSelectedCompanyId } from "../../../reducers/company.tsx";
import { useSelectedSchemeId } from "../../../reducers/scheme.tsx";
import {
  AssetPositionColumn,
  LocationColumn,
} from "../../assetRegister/AssetRegisterListPage/AssetRegisterListItem.tsx";
import BulkManualIssueEditModal from "../ManualIssue/BulkManualIssueEditModal.tsx";
import BulkManualIssueModal from "../ManualIssue/BulkManualIssueModal.tsx";
import { IssueCancelledBadge } from "./IssueCancelledBadge.tsx";
import IssueListFilterForm from "./IssueListFilterForm.tsx";
import IssueListSearchParam from "./IssueListSearchParam.tsx";
import { IssueSeverityBadge } from "./IssueSeverityBadge.tsx";
import { issueCategoryFormatter } from "./model.tsx";

function InnerListRow(props: {
  initialIssue: IssueWithAssetRegister;
}): React.ReactElement {
  const { initialIssue } = props;
  const [issue, setIssue] =
    React.useState<IssueWithAssetRegister>(initialIssue);
  const issueShouldHaveStartDate =
    issue.category !== issueCategoriesEnum["MANUAL.NOT_ADOPTED"];

  const update = useUpdateIssue(issue);
  const updateFunction = (p: {
    end_date_time?: string;
    cancelled?: boolean;
  }) => {
    return update
      .update({
        ...initialIssue,
        ...p,
      })
      .then(() => {
        setIssue({ ...issue, ...p });
      });
  };
  return (
    <StackedListThreeColumnItem
      column1={
        <LocationColumn
          location_id={issue.location_id}
          address={issue.location_address || ""}
          location_reference={issue.location_reference}
        />
      }
      column2={
        <AssetPositionColumn
          asset_position_id={issue.asset_position_id}
          use_case={issue.use_case}
          asset_position_reference={issue.asset_position_reference}
          group={issue.group}
        />
      }
      column3={
        <div className={"min-w-0 flex-auto"}>
          <div className={"flex gap-1 py-1"}>
            {issueCategoryFormatter(issue)}
            {issueShouldHaveStartDate ? (
              <>
                {" "}
                from {formatDate(issue.start_date_time)}
                {issue.end_date_time &&
                  ` to ${formatDate(issue.end_date_time)}`}
              </>
            ) : (
              <>
                {issue.end_date_time &&
                  ` until ${formatDate(issue.end_date_time)}`}
              </>
            )}
            {/*<SetEndDateModal*/}
            {/*  issue={issue}*/}
            {/*  update={updateFunction}*/}
            {/*  updating={update.loading}*/}
            {/*  updateError={update.error}*/}
            {/*/>*/}
            {/*<ManualIssueCancelButton*/}
            {/*  issue={issue}*/}
            {/*  update={updateFunction}*/}
            {/*  updating={update.loading}*/}
            {/*  updateError={update.error}*/}
            {/*/>*/}
          </div>
          <div className={"flex flex-auto gap-2 small"}>
            <span className={"text-sm my-auto"}>
              Id:{" "}
              <CopyToClipboard
                value={issue.issue_id}
                slice={8}
                iconSize={"sm"}
              />
            </span>
            {" | "}
            <IssueSeverityBadge issue={issue} />
            <IssueCancelledBadge issue={issue} />
            {" | "}
            <span className={"text-sm my-auto"}>
              Created: {formatDate(issue.raised_date_time)}
            </span>
          </div>
        </div>
      }
    />
  );
}

function IssueListRow(
  initialIssue: IssueWithAssetRegister,
): React.ReactElement {
  return (
    <InnerListRow initialIssue={initialIssue} key={initialIssue.issue_id} />
  );
}

function InnerListPage(props: {
  params?: GetIssuesIssueGetQueryParams;
}): React.ReactElement {
  const company_id = useRequiredSelectedCompanyId();
  const scheme_id = useSelectedSchemeId();

  const [lastChange, setLastChange] = React.useState(dayjs());

  const refresh = () => {
    setLastChange(dayjs());
  };
  return (
    <>
      <IssueListFilterForm filters={props.params || {}} />
      <div className={"flex float-end pt-2 gap-2"}>
        <BulkManualIssueEditModal
          initialFilters={
            {
              scheme_id: scheme_id,
              group: props.params?.group,
              asset_type: props.params?.asset_type,
              status: ["ACTIVE"],
              use_case: props.params?.use_case,
              severity: props.params?.severity,
              include_closed: props.params?.include_closed,
            } as GetIssuesIssueGetQueryParams
          }
          refresh={refresh}
        />
        <BulkManualIssueModal
          initialFilters={
            {
              scheme_id: scheme_id,
              group: props.params?.group,
              asset_type: props.params?.asset_type,
              status: ["ACTIVE"],
              use_case: props.params?.use_case,
            } as GetAssetRegisterListAssetRegisterGetQueryParams
          }
          refresh={refresh}
        />
      </div>
      <GetListPaginatedList
        params={{ company_id, scheme_id, ...props.params }}
        cursor={{ max_items: 25 }}
        header={
          <StackedListThreeColumnItem
            column1={<StackedListHeadingContent title={"Location"} />}
            column2={<StackedListHeadingContent title={"Asset Position"} />}
            column3={<StackedListHeadingContent title={"Issue"} />}
          />
        }
        additionalFunctionButtons={
          <div className={"flex gap-2"}>
            <PaginatedApiToCSVExportButton
              params={{ company_id, scheme_id, ...props.params }}
              useQuery={getIssuesIssueGet}
              // mapFunction={mapAssetRegisterItemForCSV}
              filename={
                scheme_id
                  ? `issues_list_${company_id}_${scheme_id}.csv`
                  : `issues_list_${company_id}.csv`
              }
            />
          </div>
        }
        row={IssueListRow}
        tableClassName={"list-group-flush"}
        data-cy={"issue-list"}
        useQuery={useGetIssuesIssueGet}
        lastChange={lastChange}
      />
    </>
  );
}

export default function IssueListPage(): React.ReactElement {
  return (
    <>
      <Heading>Asset Issues List</Heading>
      <BetaAlert title={"Issues List"} />
      <IssueListSearchParam
        initialState={
          {
            include_cancelled: false,
            include_closed: false,
            severity: [issueSeverityEnum.ERROR, issueSeverityEnum.WARNING],
          } as GetIssuesIssueGetQueryParams
        }
      >
        <InnerListPage />
      </IssueListSearchParam>
    </>
  );
}
